import {
  type TimeReportFilters,
  type TimeReportGroupBy,
  type TimeReportOptions,
} from "./api"
import { DATE_PRESETS, type DatePreset, datePreset } from "./dateRange"

// Filters ↔ URL. The report is bookmarkable because everything it shows is a
// query parameter; these two functions are the whole contract, and the page
// never holds filter state the URL does not agree with.

/** The sprint/epic/parent filter value for "has none" — the server's NONE. */
export const NONE = "none"
/** The tag filter value for untagged tasks — the server's LABEL_NONE. */
export const LABEL_NONE = "__untagged__"

export const EMPTY_FILTERS: TimeReportFilters = {
  companyIds: [],
  projectIds: [],
  sprintIds: [],
  epicIds: [],
  taskIds: [],
  parentIds: [],
  labels: [],
  employeeIds: [],
  from: null,
  to: null,
}

/** The default report: this month, on first visit, so the list is never empty. */
export function defaultFilters(now = new Date()): TimeReportFilters {
  const range = datePreset("this_month", now)
  return { ...EMPTY_FILTERS, from: range.from, to: range.to }
}

const LIST_KEYS = ["companyIds", "projectIds", "sprintIds", "epicIds", "taskIds", "parentIds", "labels", "employeeIds"] as const

/** Which preset a from/to pair matches, if either — so the selector shows
 *  "This month" rather than two dates the presets know how to name. */
export function presetOf(from: string | null, to: string | null, now = new Date()): DatePreset | null {
  for (const preset of DATE_PRESETS) {
    const range = datePreset(preset, now)
    if (range.from === from && range.to === to) return preset
  }
  return null
}

export function filtersFromParams(
  params: URLSearchParams | Record<string, string | undefined>,
  now = new Date(),
): TimeReportFilters {
  const get = (key: string) =>
    params instanceof URLSearchParams ? params.get(key) : (params[key] ?? null)

  const filters: TimeReportFilters = { ...EMPTY_FILTERS }
  for (const key of LIST_KEYS) {
    const raw = get(key)
    if (raw !== null) {
      filters[key] = raw.split(",").map((v) => v.trim()).filter(Boolean)
    }
  }
  const from = get("from")
  const to = get("to")
  // The default period belongs to a first visit only — a URL that names
  // neither date. Once either is in the URL the user has chosen the period,
  // and an empty value is a cleared field: an open bound, not "this month"
  // coming back.
  if (from === null && to === null) {
    const range = defaultFilters(now)
    return { ...filters, from: range.from, to: range.to }
  }
  filters.from = from || null
  filters.to = to || null
  return filters
}

export function paramsFromFilters(filters: TimeReportFilters): URLSearchParams {
  const params = new URLSearchParams()
  for (const key of LIST_KEYS) {
    if (filters[key].length) params.set(key, filters[key].join(","))
  }
  // Always written, empty when cleared: an absent date means "first visit,
  // use the default", so a cleared one has to stay in the URL to stay cleared.
  params.set("from", filters.from ?? "")
  params.set("to", filters.to ?? "")
  return params
}

export function groupByFromParams(
  params: URLSearchParams | Record<string, string | undefined>,
): TimeReportGroupBy | null {
  const raw = params instanceof URLSearchParams ? params.get("groupBy") : (params.groupBy ?? null)
  return (raw as TimeReportGroupBy) || null
}

/** The page the URL asks for, or 1. A page is position, not a filter — it
 *  never lands in the filters, but it does land in the URL. */
export function pageFromParams(
  params: URLSearchParams | Record<string, string | undefined>,
): number {
  const raw = params instanceof URLSearchParams ? params.get("page") : (params.page ?? null)
  const page = Number(raw)
  return Number.isInteger(page) && page > 1 ? page : 1
}

/** The query string the report endpoints take: the filters, the groupBy when
 *  the caller has one, and the page — omitted at 1 so a default URL stays
 *  clean. */
export function queryString(
  filters: TimeReportFilters,
  groupBy?: TimeReportGroupBy | null,
  page?: number,
): string {
  const params = paramsFromFilters(filters)
  if (groupBy) params.set("groupBy", groupBy)
  if (page && page > 1) params.set("page", String(page))
  const qs = params.toString()
  return qs ? `?${qs}` : ""
}

/** The filters without the values this company does not have, and how many
 *  were dropped.
 *
 *  A URL can carry ids the menus will never list — a link from another
 *  company, a sprint deleted since it was bookmarked, a tag no task wears any
 *  more. The server matches them to nothing, which is right, but the page would
 *  show a "1" on a filter whose menu has nothing ticked: a report filtered by
 *  something the user can neither see nor untick. So they go once the options
 *  arrive. The "none" / "No tag" sentinels belong to no list and always stay.
 *
 *  Pruned against the whole company, not the selected projects — narrowing to
 *  a project is changeProjects' job, and it runs on the user's own change. */
export function pruneUnknown(
  filters: TimeReportFilters,
  options: TimeReportOptions,
): { filters: TimeReportFilters; removed: number } {
  const known: Record<(typeof LIST_KEYS)[number], Set<string>> = {
    companyIds: new Set(options.companies.map((c) => c.id)),
    projectIds: new Set(options.projects.map((p) => p.id)),
    sprintIds: new Set([NONE, ...options.sprints.map((s) => s.id)]),
    epicIds: new Set([NONE, ...options.epics.map((e) => e.id)]),
    taskIds: new Set(options.tasks.map((t) => t.id)),
    parentIds: new Set([NONE, ...options.tasks.map((t) => t.id)]),
    labels: new Set([LABEL_NONE, ...options.labels]),
    employeeIds: new Set(options.employees.map((e) => e.id)),
  }
  const next = { ...filters }
  let removed = 0
  for (const key of LIST_KEYS) {
    const kept = filters[key].filter((value) => known[key].has(value))
    removed += filters[key].length - kept.length
    next[key] = kept
  }
  return { filters: next, removed }
}
